// src/pages/Authentication/Login.js
import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import withRouter from "../../Components/Common/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import { loginUser, resetLoginFlag } from "../../slices/thunks";
import { createSelector } from "reselect";
import eirnaLogo from "../../assets/images/eirna/eirnabio-connect-logo.png";
import LoginCarousel from "./LoginCarousel";
import ReCaptcha from "../../Components/Common/ReCaptcha";

const Login = (props) => {
  const dispatch = useDispatch();

  const selectLayoutState = (state) => state;
  const loginpageData = createSelector(selectLayoutState, (state) => ({
    error: state.Login.error,
    loading: state.Login.loading,
    errorMsg: state.Login.errorMsg,
  }));

  const { error, loading, errorMsg } = useSelector(loginpageData);
  const [passwordShow, setPasswordShow] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const recaptchaRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // If REACT_APP_RECAPTCHA_ENABLED not set to false in .env (or not present),
  // recaptcha will be enabled
  const recaptcha_enabled = process.env.REACT_APP_RECAPTCHA_ENABLED !== 'false';

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email")
        .required("Please enter your email"),
      password: Yup.string().required("Please enter your password"),
    }),
    validate: (values) => {
      const errors = {};
      if (!recaptchaToken && recaptcha_enabled) {
        setRecaptchaError("Please complete the ReCaptcha verification");
      } else {
        setRecaptchaError("");
      }
      return errors;
    },
    onSubmit: (values) => {
      // Validate ReCaptcha before submission
      if (!recaptchaToken && recaptcha_enabled) {
        setRecaptchaError("Please complete the ReCaptcha verification");
        return;
      }

      setRecaptchaError("");
      const payload = {
        ...values,
        recaptcha: recaptchaToken,
      };
      dispatch(loginUser(payload, props.router.navigate));
    },
  });

  // Enhanced ReCaptcha handlers
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (token) {
      setRecaptchaError("");
    }
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
    setRecaptchaError("ReCaptcha expired. Please verify again.");
  };

  const handleRecaptchaError = (error) => {
    setRecaptchaToken(null);
    setRecaptchaError("ReCaptcha verification failed. Please try again.");
  };

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (errorMsg) {
      setTimeout(() => {
        dispatch(resetLoginFlag());
      }, 3000);
    }
  }, [dispatch, errorMsg]);

  // Reset ReCaptcha on error
  useEffect(() => {
    if (errorMsg && recaptchaRef.current) {
      recaptchaRef.current.reset();
      setRecaptchaToken(null);
    }
  }, [errorMsg]);

  document.title = "EIRNABio Connect";

  return (
    <React.Fragment>
      <div
        style={{
          width: "100vw",
          minHeight: "100vh",
          margin: 0,
          padding: 0,
          overflow: "auto",
        }}
      >
        <Row className="g-0" style={{ minHeight: "100vh", margin: 0 }}>
          {/* Left Panel - Carousel Section */}
          {/* Left Panel - Carousel Section */}
          <Col
            xl={6}
            lg={6}
            md={6}
            className="d-none d-md-block"
            style={{
              padding: "0",
              position: "relative",
              minHeight: "100vh",
            }}
          >
            <div
              style={{
                width: "100%",
                position: "relative",
                display: "flex", // Added this
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoginCarousel />
            </div>
          </Col>

          {/* Right Panel - Login Form Section */}
          <Col
            xl={6}
            lg={6}
            md={6}
            xs={12}
            style={{
              backgroundColor: "white",
              minHeight: "100vh",
              display: "flex",
              alignItems: isMobile ? "center" : "center",
              justifyContent: "center",
              padding: isMobile ? "10px 0" : "0",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "500px",
                padding: isMobile ? "20px" : "40px",
              }}
            >
              {/* EIRNABio Logo Section */}
              <div className="text-center mb-4">
                <Link to="/" className="d-inline-block auth-logo">
                  <img
                    src={eirnaLogo}
                    alt="EIRNABio Connect"
                    height={isMobile ? "60" : "80"}
                    style={{ marginBottom: isMobile ? "15px" : "20px" }}
                  />
                </Link>
              </div>

              {/* Welcome Text */}
              <div className="text-center mb-4">
                <h5
                  style={{
                    fontWeight: "600",
                    fontSize: isMobile ? "20px" : "24px",
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  Welcome Back
                </h5>
                <p
                  style={{
                    color: "#666",
                    fontSize: isMobile ? "13px" : "14px",
                    margin: "0",
                  }}
                >
                  Enter your email and password to access your account
                </p>
              </div>

              {/* Error Alert */}
              {error && error ? (
                <Alert color="danger" className="mb-4">
                  <i className="mdi mdi-block-helper me-2"></i>
                  {error}
                </Alert>
              ) : null}

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                {/* Email Field */}
                <div className="mb-3">
                  <Label
                    htmlFor="email"
                    style={{
                      fontWeight: "500",
                      color: "#333",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Email
                  </Label>
                  <Input
                    name="email"
                    placeholder="Enter your email"
                    type="email"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email || ""}
                    invalid={
                      validation.touched.email && validation.errors.email
                        ? true
                        : false
                    }
                    style={{
                      padding: "12px 16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                  {validation.touched.email && validation.errors.email ? (
                    <FormFeedback type="invalid">
                      {validation.errors.email}
                    </FormFeedback>
                  ) : null}
                </div>

                {/* Password Field */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Label
                      htmlFor="password"
                      style={{
                        fontWeight: "500",
                        color: "#333",
                        margin: "0",
                      }}
                    >
                      Password
                    </Label>
                    <Link
                      to="/forgot-password"
                      style={{
                        color: "#0A5F3E",
                        fontSize: "12px",
                        textDecoration: "none",
                      }}
                    >
                      Forgot Password?
                    </Link>
                  </div>
                  <div className="position-relative">
                    <Input
                      name="password"
                      value={validation.values.password || ""}
                      type={passwordShow ? "text" : "password"}
                      placeholder="Enter your password"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        validation.touched.password &&
                        validation.errors.password
                          ? true
                          : false
                      }
                      style={{
                        padding: "12px 16px",
                        paddingRight: "45px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        fontSize: "14px",
                      }}
                    />
                    <button
                      className="btn position-absolute"
                      type="button"
                      onClick={() => setPasswordShow(!passwordShow)}
                      style={{
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        border: "none",
                        background: "none",
                        color: "#666",
                        padding: "0",
                        width: "20px",
                        height: "20px",
                      }}
                    >
                      <i
                        className={
                          passwordShow ? "ri-eye-off-fill" : "ri-eye-fill"
                        }
                        style={{ fontSize: "16px" }}
                      ></i>
                    </button>
                  </div>
                  {validation.touched.password && validation.errors.password ? (
                    <FormFeedback type="invalid" style={{ display: "block" }}>
                      {validation.errors.password}
                    </FormFeedback>
                  ) : null}
                </div>

                {/* Remember Me */}
                <div className="form-check mb-1">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    name="rememberMe"
                    id="auth-remember-check"
                    onChange={validation.handleChange}
                    checked={validation.values.rememberMe}
                    style={{
                      marginTop: "4px",
                    }}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="auth-remember-check"
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      marginLeft: "8px",
                    }}
                  >
                    Remember me
                  </Label>
                </div>

                {/* ReCaptcha */}
                {recaptcha_enabled && (
                  <div className="mb-2 d-flex justify-content-center">
                    <ReCaptcha
                      ref={recaptchaRef}
                      siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={handleRecaptchaExpired}
                      onError={handleRecaptchaError}
                      theme="light"
                      size="normal"
                      hasError={!!recaptchaError}
                      errorMessage={recaptchaError}
                    />
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  color="success"
                  disabled={error ? null : loading ? true : false}
                  className="w-100"
                  type="submit"
                  style={{
                    backgroundColor: "#0A5F3E",
                    borderColor: "#0A5F3E",
                    padding: "12px",
                    fontSize: "16px",
                    fontWeight: "500",
                    borderRadius: "8px",
                    border: "none",
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2">
                        Loading...
                      </Spinner>
                      Signing In...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </Form>

              {/* Sign Up Section */}
              <div className="text-center mt-4">
                <p style={{ fontSize: "14px", color: "#666", margin: "0" }}>
                  Don't have an account?{" "}
                  <Link
                    to="/register"
                    style={{
                      color: "#0A5F3E",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    Sign Up
                  </Link>
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Login);
